// v2.17.0 — Page publique unifiée pour Administration et Finance.
// Détecte le type via `prop kind` ("admin" | "finance") et fetch l'endpoint approprié.

import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "") + "/api";

const CONFIG = {
  admin: {
    endpoint: "/v3/administration",
    accent: "#3B82F6",
    labelMode: "🛂 Administration",
    titleField: "service_name",
    subtitleField: "organism",
    priceField: "cost_display",
    showDocs: true,
  },
  finance: {
    endpoint: "/v3/finance",
    accent: "#059669",
    labelMode: "💰 Finance",
    titleField: "name",
    subtitleField: "type",
    priceField: "rate_display",
    showDocs: false,
  },
};

export default function DirectoryDetailPage({ kind }) {
  const cfg = CONFIG[kind] || CONFIG.admin;
  const { id } = useParams();
  const nav = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}${cfg.endpoint}/${id}`)
      .then((r) => { if (!cancelled) { setItem(r.data?.item || null); setLoading(false); } })
      .catch(() => { if (!cancelled) { setError("Fiche introuvable"); setLoading(false); } });
    return () => { cancelled = true; };
  }, [id, cfg.endpoint]);

  const copy = async () => {
    if (!item) return;
    const title = item[cfg.titleField] || "";
    const sub = item[cfg.subtitleField] || "";
    const text = `${title}\n${sub}\n📍 ${item.location_text || ''}\n📞 ${item.phone || ''}\n🌐 ${item.website || ''}`;
    try { await navigator.clipboard.writeText(text); toast.success("📋 Copié"); }
    catch (_) { toast.error("Copie impossible"); }
  };
  const share = () => {
    if (!item) return;
    const title = item[cfg.titleField] || "";
    const sub = item[cfg.subtitleField] || "";
    const msg = `📋 ${title}\n${sub}\n${item.location_text || ''}\n📞 ${item.phone || ''}\n${item.website ? '🌐 ' + item.website : ''}\n\nVu sur CIV-AI 🇨🇮`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(180deg, #0B1220 0%, #0A1029 100%)" }}>
      <header style={{ padding: "12px 20px", borderBottom: "1px solid rgba(255,255,255,0.06)", color: "#fff" }}>
        <a href="/" style={{ color: "#fff", textDecoration: "none", fontWeight: 800, fontSize: 14 }}>🇨🇮 CIV-AI · {cfg.labelMode}</a>
      </header>
      <Toaster richColors position="top-center" />
      {loading && <div data-testid="directory-detail-loading" style={{ color: "#cbd5e1", padding: 40, textAlign: "center" }}>Chargement…</div>}
      {(error || (!loading && !item)) && (
        <div data-testid="directory-detail-error" style={{ color: "#fda4af", padding: 40, textAlign: "center" }}>
          ⚠️ {error || "Fiche introuvable"}
          <div style={{ marginTop: 16 }}>
            <button onClick={() => nav("/")} style={btnSecondary}>← Retour à CIV-AI</button>
          </div>
        </div>
      )}
      {item && (
        <div data-testid={`directory-detail-${kind}`} style={{
          maxWidth: 820, margin: "0 auto", padding: "28px 20px",
          color: "#F1F5F9", fontFamily: "system-ui, -apple-system, sans-serif",
        }}>
          <button onClick={() => nav(-1)} data-testid="directory-detail-back" style={btnBack}>← Retour</button>

          <div style={{
            background: `linear-gradient(135deg, ${cfg.accent}40 0%, ${cfg.accent}10 100%)`,
            border: `1px solid ${cfg.accent}55`, borderRadius: 16, padding: "20px 22px", marginBottom: 22,
          }}>
            <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
              <span style={pill("rgba(15,23,42,0.85)", "#fff")}>{item.category_emoji} {item.category_label}</span>
              {item.verified && <span style={pill("#fff", cfg.accent)}>✓ Vérifiée</span>}
              {item.online_available && <span style={pill(cfg.accent, "#fff")}>🌐 Dispo en ligne</span>}
            </div>
            <h1 data-testid="directory-detail-title" style={{ fontSize: 24, margin: "4px 0 6px", lineHeight: 1.2 }}>{item[cfg.titleField]}</h1>
            <div style={{ fontSize: 14, color: "#cbd5e1" }}>{item[cfg.subtitleField]}</div>
            <div style={{ fontSize: 13, color: "#94a3b8", marginTop: 4 }}>📍 {item.location_text}</div>
          </div>

          {item.website && (
            <button data-testid="directory-detail-site-btn" onClick={() => window.open(item.website, "_blank", "noopener,noreferrer")} style={{
              width: "100%", background: cfg.accent, color: "#fff", border: "none",
              padding: "13px 18px", borderRadius: 12, cursor: "pointer", fontWeight: 800, fontSize: 14,
              marginBottom: 10, boxShadow: `0 4px 14px ${cfg.accent}55`,
            }}>🌐 Visiter le site officiel</button>
          )}
          {item.online_url && item.online_url !== item.website && (
            <button data-testid="directory-detail-online-btn" onClick={() => window.open(item.online_url, "_blank", "noopener,noreferrer")} style={{
              width: "100%", background: "#0EA5E9", color: "#fff", border: "none",
              padding: "11px 16px", borderRadius: 10, cursor: "pointer", fontWeight: 700, fontSize: 13.5, marginBottom: 14,
            }}>📲 Démarche en ligne</button>
          )}
          <div style={{ display: "flex", gap: 8, marginBottom: 22, flexWrap: "wrap" }}>
            {item.phone && (
              <button onClick={() => { window.location.href = `tel:${item.phone.replace(/\s+/g, '')}`; }} data-testid="directory-detail-call-btn" style={{ ...btnSecondary, background: "#16A34A", color: "#fff", borderColor: "transparent" }}>📞 {item.phone}</button>
            )}
            <button onClick={copy} data-testid="directory-detail-copy-btn" style={btnSecondary}>📋 Copier</button>
            <button onClick={share} data-testid="directory-detail-share-btn" style={{ ...btnSecondary, background: "#25D366", color: "#fff", borderColor: "transparent" }}>📤 WhatsApp</button>
          </div>

          <Block icon="📋" title="Détails">
            {item.cost_display !== undefined && <Row k="Coût" v={item.cost_display || "—"} />}
            {item.duration && <Row k="Durée" v={item.duration} />}
            {item.rate_display && <Row k="Taux indicatif" v={item.rate_display} />}
            <Row k="Adresse" v={item.location_text} />
          </Block>

          <Block icon="📝" title="Description">
            <pre style={{ whiteSpace: "pre-wrap", fontFamily: "inherit", fontSize: 13.5, color: "#cbd5e1", lineHeight: 1.65, margin: 0 }}>
              {item.description_full || item.description_short || ""}
            </pre>
          </Block>

          {cfg.showDocs && Array.isArray(item.documents_required) && item.documents_required.length > 0 && (
            <Block icon="📎" title="Documents requis">
              <ul style={{ margin: 0, paddingLeft: 22, color: "#cbd5e1", fontSize: 13, lineHeight: 1.7 }}>
                {item.documents_required.map((d) => <li key={d}>{d}</li>)}
              </ul>
            </Block>
          )}

          {Array.isArray(item.products) && item.products.length > 0 && (
            <Block icon="📦" title="Produits proposés">
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {item.products.map((p) => (
                  <span key={p} style={{
                    background: `${cfg.accent}1A`, border: `1px solid ${cfg.accent}55`,
                    color: "#fff", padding: "4px 10px", borderRadius: 999,
                    fontSize: 12, fontWeight: 600,
                  }}>{p}</span>
                ))}
              </div>
            </Block>
          )}
        </div>
      )}
    </div>
  );
}

function Block({ icon, title, children }) {
  return (
    <section style={{ marginBottom: 18, padding: 16, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12 }}>
      <h3 style={{ margin: "0 0 10px", fontSize: 14, fontWeight: 700, color: "#FBBF24" }}>{icon} {title}</h3>
      {children}
    </section>
  );
}
function Row({ k, v }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid rgba(255,255,255,0.05)", fontSize: 13 }}>
      <span style={{ color: "#94a3b8" }}>{k}</span>
      <span style={{ color: "#fff", fontWeight: 600, textAlign: "right", maxWidth: "60%" }}>{v}</span>
    </div>
  );
}
const pill = (bg, color) => ({ background: bg, color, padding: "4px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700 });
const btnSecondary = { background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.15)", color: "#fff", padding: "8px 14px", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 13 };
const btnBack = { background: "transparent", border: "1px solid rgba(255,255,255,0.15)", color: "#cbd5e1", padding: "6px 14px", borderRadius: 999, fontSize: 12.5, cursor: "pointer", marginBottom: 18 };
